import React from "react";

import { Trans } from "@lingui/react/macro";
import { scaleLinear } from "@visx/scale";
import type { ScaleLinear } from "d3-scale";
import { maxBy, toUpper } from "lodash/fp";
import { DateTime } from "luxon";

import Typography from "@mui/material/Typography";
import { DayCalendarSlotProps } from "@mui/x-date-pickers/DateCalendar/DayCalendar";

import LoadSpinner from "@readai/common/components/LoadSpinner";
import Stack, { Row } from "@readai/pulse/components/Stack";

import {
  Calendar,
  CalendarDay,
  Container,
  DateCalendarDateTimeProps,
  Legend,
  LoadingContainer,
  PickersDayDateTimeProps,
} from "./styles";

export type DayProps = {
  [key: string]: {
    availabilityScore: number;
  };
};

type CalendarViewProps = DateCalendarDateTimeProps & {
  dayProps?: DayProps;
  loading?: boolean;
};
type PickerDayProps = PickersDayDateTimeProps & {
  dayProps?: DayProps;
  colorScale?: ScaleLinear<number, React.CSSProperties["color"]>;
};

const getColorScale = (maxScore: number) =>
  scaleLinear<React.CSSProperties["color"]>({
    domain: [0, 1, maxScore],
    range: ["#FFF", "#E8F9F5", "#A3E9D9"],
  });

const Day = ({ colorScale, dayProps = {}, day, ...props }: PickerDayProps) => {
  const formattedDay = day.toFormat("yyyy-MM-dd");
  const propsForDay = dayProps[formattedDay] ?? {};
  const backgroundColor =
    props.disabled || !colorScale
      ? undefined
      : colorScale(propsForDay.availabilityScore ?? 0);

  return (
    <CalendarDay
      {...props}
      day={day}
      disableRipple
      backgroundColor={backgroundColor}
    />
  );
};

const CalendarView = ({
  dayProps = {},
  loading = false,
  ...props
}: CalendarViewProps) => {
  const maxScore =
    maxBy("availabilityScore", Object.values(dayProps))?.availabilityScore ?? 1;
  const colorScale = getColorScale(maxScore);

  return (
    <Stack spacing={3} variant="center" paddingTop={1}>
      <Row variant="center" spacing={1} sx={{ color: "text.med" }}>
        <Typography variant="labelReg">
          <Trans>Less availability</Trans>
        </Typography>
        <Legend />
        <Typography variant="labelReg">
          <Trans>More availability</Trans>
        </Typography>
      </Row>
      <Container>
        <Calendar
          views={["day"]}
          dayOfWeekFormatter={(date) => toUpper(date.toFormat("EEE"))}
          fixedWeekNumber={6}
          showDaysOutsideCurrentMonth
          {...props}
          loading={loading}
          slots={{ day: Day }}
          // typecast since it's totally valid to pass custom props here,
          // but the type checker wants it to only be the natively-supported
          // PickerDayProps
          slotProps={{
            day: {
              dayProps,
              colorScale,
            } as DayCalendarSlotProps<DateTime>["day"],
          }}
        />
        {loading && (
          <LoadingContainer variant="center">
            <LoadSpinner />
          </LoadingContainer>
        )}
      </Container>
    </Stack>
  );
};

export default CalendarView;
